import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Paper,
  Button,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete
} from '@mui/material';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import CameraScanner from '../components/CameraScanner';
import { addOrder } from '../services/orderService';
import { getProductsByOrgId } from '../services/productService';
import { getCustomers, addCustomer } from '../services/customerService';
import { OrgContext } from '../contexts/OrgContext';
import { CurrencyContext } from '../contexts/CurrencyContext';
import BackButton from '../components/BackButton';
import customerSchema from '../models/customerSchema';
import DynamicForm from '../components/DynamicForm';
import formatDateToStandard from '../utils/DateFormatter';

const POSOrderPage = () => {
  const location = useLocation();
  
  // Set default values that will definitely work
  const defaultValues = {
    order_type: 'Sale',
    order_status: 'Completed',
    order_date: new Date().toISOString().slice(0, 10),
    delivery_date: new Date().toISOString().slice(0, 10)
  };

  const methods = useForm({
    mode: 'onChange', // Change from 'onSubmit' to 'onChange'
    defaultValues, // Set the default values here
  });
  const { control, setValue } = methods;
  const navigate = useNavigate();
  const { orgId } = useContext(OrgContext);
  const { currency } = useContext(CurrencyContext);
  
  const [inventoryOptions, setInventoryOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addedOrder, setAddedOrder] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [addingNewCustomer, setAddingNewCustomer] = useState(false);
  const [productLineItems, setProductLineItems] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  
  const customerForm = useForm();
  const { handleSubmit: handleCustomerSubmit } = customerForm;

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$',
    CHF: 'CHF', CNY: '¥', INR: '₹', JPY: '¥', BDT: '৳',
  };
  const symbol = currencySymbols[currency] || currency;

  // Add a watch for product_line_items to ensure we track changes
  const product_line_items = useWatch({
    control: control,
    name: 'product_line_items',
    defaultValue: []
  });

  // Update useEffect to also sync with the watched value
  useEffect(() => {
    if (product_line_items && product_line_items.length > 0) {
      setProductLineItems(product_line_items);
    }
  }, [product_line_items]);

  // Initialize form with default values from state
  useEffect(() => {
    // Always set the defaults first
    setValue('order_type', defaultValues.order_type);
    setValue('order_status', defaultValues.order_status);
    setValue('order_date', defaultValues.order_date);
    setValue('delivery_date', defaultValues.delivery_date);
    setValue('product_line_items', []); // Initialize with empty array
    
    // Then override with location state if available
    if (location.state) {
      console.log('Received location state:', location.state);
      Object.entries(location.state).forEach(([key, value]) => {
        if (value !== undefined) {
          setValue(key, value);
        }
      });
      
      // Set product line items in state for our local usage
      if (location.state.product_line_items) {
        setProductLineItems(location.state.product_line_items);
      }
    }
    // We intentionally only run once for initial population; defaultValues are static.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, location.state]);

  useEffect(() => {
    // Load products and customers
    if (orgId) {
      const intOrgId = parseInt(orgId);
      
      setLoading(true);
      Promise.all([
        // Fetch all products (limit=0 means no pagination)
        getProductsByOrgId(intOrgId, { page: 1, limit: 0, sortBy: 'product_name', sortOrder: 'ASC' }),
        getCustomers(intOrgId)
      ]).then(([productsResponse, customersResponse]) => {
        // Extract data from response objects
        const products = productsResponse.data || productsResponse;
        const customers = customersResponse.data || customersResponse;
        
        setInventoryOptions(products.map(product => ({
          label: product.product_name,
          value: product.id.toString(),
          sku: product.sku,
          unit_price: product.current_unit_price,
          unit_cost: product.current_unit_cost,
          scannerReference: product.scanner_reference
        })));
        
        setCustomerOptions(customers);
        setLoading(false);
      }).catch(err => {
        console.error('Error loading data:', err);
        setLoading(false);
      });
    }
  }, [orgId]);

  const findInventoryOptionByCode = React.useCallback((code) => {
    if (!code) return null;
    const upper = code.toString().trim().toUpperCase();
    return (inventoryOptions || []).find((opt) => {
      const refs = [opt.scannerReference, opt.sku]
        .filter(Boolean)
        .map((v) => v.toString().trim().toUpperCase());
      return refs.includes(upper);
    }) || null;
  }, [inventoryOptions]);

  const handleProductLineItemsChange = React.useCallback((items) => {
    setProductLineItems(items);
    setValue('product_line_items', items);
  }, [setValue]);

  const addScannedProductToCart = React.useCallback((opt) => {
    if (!opt) return;
    setProductLineItems((prev) => {
      const list = prev || [];
      const idx = list.findIndex((it) => it.product_id === opt.value);
      if (idx >= 0) {
        const updated = [...list];
        const q = (updated[idx].quantity || 0) + 1;
        const unit = parseFloat(updated[idx].unit_price || opt.unit_price || 0);
        updated[idx] = { ...updated[idx], quantity: q, total_price: q * unit };
        // also sync form
        setValue('product_line_items', updated);
        return updated;
      } else {
        const unit = parseFloat(opt.unit_price || 0);
        const newItem = {
          product_id: opt.value,
          product_name: opt.label,
          sku: opt.sku,
          unit_price: unit,
          original_unit_price: unit,
          quantity: 1,
          total_price: unit * 1,
          discount_type: 'none',
          discount_value: 0,
        };
        const updated = [...list, newItem];
        setValue('product_line_items', updated);
        return updated;
      }
    });
  }, [setValue]);

  const removeScannedProductFromCart = React.useCallback((productId) => {
    setProductLineItems((prev) => {
      const list = prev || [];
      const idx = list.findIndex((it) => it.product_id === productId);
      if (idx === -1) return list;
      const current = list[idx];
      const qty = parseInt(current.quantity || 0, 10);
      if (qty > 1) {
        const unit = parseFloat(current.unit_price || 0);
        const updated = [...list];
        const newQty = qty - 1;
        updated[idx] = { ...current, quantity: newQty, total_price: unit * newQty };
        setValue('product_line_items', updated);
        return updated;
      }
      const updated = list.filter((it) => it.product_id !== productId);
      setValue('product_line_items', updated);
      return updated;
    });
  }, [setValue]);

  const calculateOrderTotals = (lineItems) => {
    // Simple calculation without tax for now
    const subtotal = lineItems.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0);
    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      taxAmount: 0,
      total: parseFloat(subtotal.toFixed(2))
    };
  };

  const handleProceedToCheckout = () => {
    if (productLineItems.length === 0) {
      setErrorMessage('Please add at least one product to the order.');
      return;
    }
    setShowCustomerModal(true);
  };

  const handleCustomerSelection = (customer) => {
    setSelectedCustomer(customer);
    setAddingNewCustomer(false);
  };

  const handleAddNewCustomerClick = () => {
    setAddingNewCustomer(true);
  };

  const handleCancelNewCustomer = () => {
    setAddingNewCustomer(false);
  };

  const handleAddNewCustomer = async (data) => {
    try {
      const response = await addCustomer(orgId, data);
      const newCustomer = response;
      setCustomerOptions([...customerOptions, newCustomer]);
      setSelectedCustomer(newCustomer);
      setAddingNewCustomer(false);
    } catch (error) {
      console.error('Error adding new customer:', error);
      setErrorMessage('Failed to add new customer. Please try again.');
    }
  };

  const handleCompleteOrder = () => {
    if (!selectedCustomer) {
      setErrorMessage('Please select or add a customer to continue.');
      return;
    }

    const totals = calculateOrderTotals(productLineItems);
    
    // Get the current form values to ensure we're using the correct order_type and order_status
    const formValues = methods.getValues();

    const processedData = {
      org_id: parseInt(orgId),
      order_type: formValues.order_type || 'Sale',
      order_status: formValues.order_status || 'Completed',
      order_date: formValues.order_date || new Date().toISOString().slice(0, 10),
      delivery_date: formValues.delivery_date || new Date().toISOString().slice(0, 10),
      customer_id: selectedCustomer.id,
      line_items: productLineItems.map(item => ({
        product_id: parseInt(item.product_id, 10),
        quantity: parseInt(item.quantity, 10),
        unit_price: parseFloat(item.unit_price),
        total_price: parseFloat(item.unit_price) * parseInt(item.quantity, 10),
        original_unit_price: parseFloat(item.unit_price),
        discount_type: item.discount_type || 'none',
        discount_value: item.discount_value ? parseFloat(item.discount_value) : 0
      })),
      amount: totals.total,
      meta: {
        tax_percentage: 0,
        tax_amount: 0,
        subtotal: totals.subtotal,
        deliveryAddress: null,
        pos_order: true // Mark as POS order
      },
      payment_type: 'cash', // Default to cash for POS
      payment_due_date: null,
    };

    // Close the customer selection dialog immediately when Complete Order is clicked
    setShowCustomerModal(false);
    
    setLoading(true);
    addOrder(processedData)
      .then(response => {
        setAddedOrder(response);
      })
      .catch(error => {
        console.error('Error adding order:', error);
        setErrorMessage(error.response?.data?.message || 'Failed to add order. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleAddNewOrder = () => {
    setAddedOrder(null);
    setProductLineItems([]);
    setSelectedCustomer(null);
  };

  const renderOrderDetails = (order) => {
    return (
      <Box sx={{ 
        '& .MuiTypography-root': { 
          mb: 1,
          color: 'text.primary' 
        }
      }}>
        <Typography><strong>Order ID:</strong> {order.id}</Typography>
        <Typography><strong>Order Type:</strong> {order.order_type}</Typography>
        <Typography><strong>Order Date:</strong> {formatDateToStandard(order.order_date)}</Typography>
        <Typography><strong>Order Status:</strong> {order.order_status}</Typography>
        <Typography><strong>Total Amount:</strong> {symbol}{parseFloat(order.amount).toFixed(2)}</Typography>
        
        {order.customer_id && (
          <Typography><strong>Customer ID:</strong> {order.customer_id}</Typography>
        )}

        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Line Items:</Typography>
        {order.lineItems.map((item, index) => (
          <Box key={index} sx={{ ml: 2, mb: 2 }}>
            <Typography><strong>Product:</strong> {item.product.product_name}</Typography>
            <Typography><strong>Quantity:</strong> {item.quantity}</Typography>
            <Typography><strong>Unit Price:</strong> {symbol}{parseFloat(item.unit_price).toFixed(2)}</Typography>
            <Typography><strong>Total Price:</strong> {symbol}{parseFloat(item.total_price).toFixed(2)}</Typography>
            <Typography><strong>SKU:</strong> {item.product.sku}</Typography>
          </Box>
        ))}
        <Typography><strong>Total Amount:</strong> {symbol}{parseFloat(order.amount).toFixed(2)}</Typography>
      </Box>
    );
  };

  return (
    <Paper
      sx={{
        p: 3,
        maxWidth: 900,
        mx: 'auto',
        backgroundColor: '#1e1e1e',
        color: 'text.primary',
      }}
    >
      <BackButton />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : addedOrder ? (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: 'success.main' }}>
            POS Order Added Successfully
          </Typography>
          <Box sx={{ 
            my: 3, 
            p: 2, 
            backgroundColor: 'background.paper',
            borderRadius: 1,
          }}>
            {renderOrderDetails(addedOrder)}
          </Box>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAddNewOrder} 
            sx={{ mt: 2, mr: 2 }}
          >
            New POS Order
          </Button>
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={() => navigate(`/${orgId}/orders`)}
            sx={{ mt: 2 }}
          >
            View Orders
          </Button>
        </Box>
      ) : (
        <>
          <Typography variant="h5" gutterBottom>
            POS Order
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
            Scan products or add them manually, then proceed to checkout
          </Typography>
          
          <FormProvider {...methods}>
            <form>
              {/* Hidden fields are set through setValue but not displayed */}
              
              {/* Only show product line items */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button variant="outlined" onClick={() => setScanOpen(true)}>Scan via Camera</Button>
                </Box>
                <DynamicForm
                  schema={[
                    {
                      name: "product_line_items",
                      label: "Products",
                      type: "product-line-items",
                      required: true
                    }
                  ]}
                  control={control}
                  errors={methods.formState.errors}
                  inventoryOptions={inventoryOptions}
                   // Add onChange handler to update state properly
                   onChange={handleProductLineItemsChange}
                />
              </Box>
              
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} textAlign="right">
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleProceedToCheckout}
                    disabled={!product_line_items?.length} // Use the watched value to determine disabled state
                    size="large"
                  >
                    Proceed to Checkout
                  </Button>
                  {!product_line_items?.length && (
                    <Typography 
                      variant="caption" 
                      color="error" 
                      sx={{ display: 'block', mt: 1, textAlign: 'right' }}
                    >
                      Add at least one product to enable checkout
                    </Typography>
                  )}
                  
                  {/* Display count of products added as a helpful indicator */}
                  {product_line_items?.length > 0 && (
                    <Typography 
                      variant="caption" 
                      color="success.main"
                      sx={{ display: 'block', mt: 1, textAlign: 'right' }}
                    >
                      {product_line_items.length} product{product_line_items.length > 1 ? 's' : ''} added to cart
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </form>
          </FormProvider>
          <CameraScanner
            open={scanOpen}
            onClose={() => setScanOpen(false)}
            onDetected={(code) => {
              const opt = findInventoryOptionByCode(code);
              if (opt) addScannedProductToCart(opt);
            }}
            continuous
            title="Scan Products"
            scannedItems={(productLineItems || []).map((it) => ({ id: it.product_id, name: it.product_name, qty: it.quantity }))}
            onRemoveItem={(id) => removeScannedProductFromCart(id)}
          />
        </>
      )}
      
      {/* Customer Selection Dialog */}
      <Dialog 
        open={showCustomerModal && !addingNewCustomer} 
        onClose={() => setShowCustomerModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Customer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Please select an existing customer or add a new one to complete the order.
          </Typography>
          <Autocomplete
            options={customerOptions}
            getOptionLabel={(option) => 
              `${option.customer_name} (ID: ${option.id}, Phone: ${option.contact_number})`
            }
            value={selectedCustomer}
            onChange={(event, newValue) => handleCustomerSelection(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Search Customers" fullWidth variant="outlined" margin="normal" />
            )}
          />
          <Button 
            variant="outlined" 
            onClick={handleAddNewCustomerClick}
            sx={{ mt: 2, display: 'block' }}
          >
            Add New Customer
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCustomerModal(false)}>Cancel</Button>
          <Button 
            onClick={handleCompleteOrder} 
            variant="contained" 
            color="primary"
            disabled={!selectedCustomer}
          >
            Complete Order
          </Button>
          {!selectedCustomer && (
            <Typography 
              variant="caption" 
              color="error" 
              sx={{ position: 'absolute', bottom: 16, left: 24 }}
            >
              Please select a customer to continue
            </Typography>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Add New Customer Dialog */}
      <Dialog 
        open={showCustomerModal && addingNewCustomer} 
        onClose={handleCancelNewCustomer}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Fill in customer details to create a new customer and complete the order.
          </Typography>
          <FormProvider {...customerForm}>
            <form onSubmit={handleCustomerSubmit(handleAddNewCustomer)}>
              <DynamicForm 
                schema={customerSchema} 
                control={customerForm.control} 
                errors={customerForm.formState.errors} 
              />
            </form>
          </FormProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNewCustomer}>Cancel</Button>
          <Button 
            onClick={customerForm.handleSubmit(handleAddNewCustomer)} 
            variant="contained" 
            color="primary"
          >
            Add Customer
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default POSOrderPage;
